import React from "react";
import { Icon } from "semantic-ui-react";
import {
    formatCallGroupMemberStatus,
    formatCallGroupPriorityCell,
    formatCallGroupRequiredCell,
    formatCallGroupNameCell,
    callGroupEscalationColor,
} from "./callGroupCustomerFormat";

/** Call Information card only when a real group name exists (not null / blank). */
function hasNonEmptyCallGroupName(customer) {
    if (!customer) return false;
    const n = customer.call_group_name;
    return n != null && String(n).trim() !== "";
}

function getDynamicVariablesList(customer) {
    const df =
        customer.dynamic_variables ||
        customer.dynamicVariables ||
        customer.dynamic_fields ||
        customer.dynamicFields;
    if (Array.isArray(df)) return df;
    if (df && typeof df === "object") {
        return Object.entries(df).map(([k, v]) => ({ field_name: k, field_value: v }));
    }
    return [];
}

function InfoRow({ icon, label, value, valueNode }) {
    return (
        <div className="ov-cd-info-row">
            <div className="ov-cd-info-row__left">
                <Icon name={icon} className="ov-cd-info-row__icon" />
                <span className="ov-cd-info-row__label">{label}</span>
            </div>
            <div className="ov-cd-info-row__value">{valueNode ?? value}</div>
        </div>
    );
}

/**
 * Outbound Voice — Customer Details modal body.
 * Group name row and Call Information card render only when `call_group_name` is non-empty (trimmed).
 */
export default function CustomerDetailsModalBody({
    selectedCustomer,
    phoneDisplay,
    showEmptyDynamicVariablesMessage = true,
}) {
    if (!selectedCustomer) {
        return (
            <div className="outbound-voice-customer-details-modal">
                <div className="text-muted">No customer selected.</div>
            </div>
        );
    }

    const showCallInformationCard = hasNonEmptyCallGroupName(selectedCustomer);
    const dynList = getDynamicVariablesList(selectedCustomer);
    const escalationLabel = formatCallGroupMemberStatus(selectedCustomer.call_group_member_status);
    const escalationColor = callGroupEscalationColor(selectedCustomer.call_group_member_status);

    return (
        <div className="outbound-voice-customer-details-modal">
            <div className="ov-cd-top-grid">
                <div className="ov-cd-card">
                    <div className="ov-cd-card__title">
                        <span className="ov-cd-card__title-icon" aria-hidden="true">
                            <Icon name="user" />
                        </span>
                        <span>Customer Information</span>
                    </div>
                    <InfoRow icon="user" label="Name" value={selectedCustomer.name || "N/A"} />
                    <InfoRow icon="phone" label="Phone" value={phoneDisplay || "N/A"} />
                    {hasNonEmptyCallGroupName(selectedCustomer) && (
                        <InfoRow
                            icon="users"
                            label="Group name"
                            value={formatCallGroupNameCell(selectedCustomer)}
                        />
                    )}
                </div>

                {showCallInformationCard && (
                    <div className="ov-cd-card">
                        <div className="ov-cd-card__title">
                            <span className="ov-cd-card__title-icon" aria-hidden="true">
                                <Icon name="phone square" />
                            </span>
                            <span>Call Information</span>
                        </div>
                        <InfoRow
                            icon="flag"
                            label="Priority"
                            valueNode={
                                <span className="ov-cd-badge ov-cd-badge--priority">
                                    {formatCallGroupPriorityCell(selectedCustomer)}
                                </span>
                            }
                        />
                        <InfoRow
                            icon="clipboard"
                            label="Required"
                            valueNode={
                                <span className="ov-cd-badge ov-cd-badge--required">
                                    {formatCallGroupRequiredCell(selectedCustomer)}
                                </span>
                            }
                        />
                        <InfoRow
                            icon="level up"
                            label="Escalation"
                            valueNode={
                                <span
                                    className="ov-cd-badge ov-cd-badge--escalation"
                                    style={{
                                        color: escalationColor,
                                        backgroundColor: `${escalationColor}22`,
                                        borderColor: `${escalationColor}44`,
                                    }}
                                >
                                    {escalationLabel}
                                </span>
                            }
                        />
                    </div>
                )}
            </div>

            {dynList.length === 0 ? (
                showEmptyDynamicVariablesMessage ? (
                    <div className="ov-cd-dyn-empty-state" role="status">
                        <p className="ov-cd-dyn-empty">No dynamic variables.</p>
                    </div>
                ) : null
            ) : (
                <div className="ov-cd-dynamic-block">
                    <div className="ov-cd-dynamic-block__title">
                        <Icon name="database" />
                        <span>Dynamic Variables</span>
                    </div>
                    <p className="ov-cd-dynamic-block__hint">
                        Spaces in CSV column names are removed (e.g. &quot;Policy Number&quot; → PolicyNumber). Use{" "}
                        <code className="ov-cd-var-code">
                            {"{{"}VariableName{"}}"}
                        </code>{" "}
                        in your workspace to insert the value during calls.
                    </p>
                    <div className="ov-cd-dyn-table-wrap">
                        <table className="ov-cd-dyn-table">
                            <thead>
                                <tr>
                                    <th>Variable name</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dynList.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="ov-cd-dyn-table__name">
                                            {item.field_name || item.name || item.key || "Field"}
                                        </td>
                                        <td className="ov-cd-dyn-table__val">
                                            {item.field_value ?? item.value ?? ""}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
